import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/ventrix/PublicLayout";
import { useApiQuery } from "@/hooks/useApi";
import { iconFor } from "@/lib/serviceIcons";
import { Search, ArrowRight, Ship, Plane, Truck, ShieldCheck, Warehouse, FileCheck2, Building2, Globe2, Clock3, Zap } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1575528941322-c74397246f19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxjb250YWluZXIlMjBzaGlwJTIwb2NlYW58ZW58MHx8fHwxNzg0MTMyNTY5fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [numbers, setNumbers] = useState("");
  const nav = useNavigate();
  // Retry a few times so a request during backend warm-up (right after a deploy)
  // still fills the services section instead of leaving it empty.
  const { data: svcData } = useApiQuery("public-services", "/v1/public/services", { retry: 4 });
  const services = svcData?.items || [];
  const track = (e) => {
    e.preventDefault();
    if (numbers.trim()) nav(`/track?numbers=${encodeURIComponent(numbers.trim())}`);
  };

  return (
    <div className="min-h-screen">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(14,47,118,0.92) 0%, rgba(14,47,118,0.78) 50%, rgba(14,47,118,0.55) 100%)" }} />
          <div className="absolute inset-0 vtx-hero-grid opacity-40" />
        </div>
        <div className="relative vtx-container pt-20 pb-28 md:pt-28 md:pb-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-[#A9C0E0] mb-6">
              <span className="w-6 h-px bg-[#A9C0E0]" /> Carrier-grade logistics · est. 2004
            </div>
            <h1 className="text-white font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
              Move cargo like it<br />
              <span className="text-[#A9C0E0]">actually matters.</span>
            </h1>
            <p className="mt-6 text-[#DDE7F5] text-base md:text-lg max-w-xl leading-relaxed">
              Air, sea and road freight with milestone-accurate tracking, transparent pricing, and an ops team that answers when things go sideways.
            </p>

            {/* Tracking widget — the hero */}
            <form onSubmit={track} className="mt-10 bg-white shadow-2xl border border-[#A9C0E0]/40 p-5 md:p-6" style={{ borderRadius: 3 }} data-testid="hero-track-form">
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <div className="flex-1">
                  <label className="vtx-eyebrow block mb-2">Tracking number</label>
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2F76]" />
                    <input
                      className="vtx-input pl-10 font-mono tracking-wider"
                      placeholder="VTX8HD73KP2Q  ·  paste up to 10 comma-separated"
                      value={numbers}
                      onChange={(e) => setNumbers(e.target.value)}
                      data-testid="hero-track-input"
                    />
                  </div>
                </div>
                <button type="submit" className="vtx-btn-danger inline-flex items-center gap-2 justify-center whitespace-nowrap" data-testid="hero-track-submit">
                  Track shipment <ArrowRight size={16} />
                </button>
              </div>
              <div className="mt-3 text-xs text-[#475569]">
                Or <Link to="/quote" className="vtx-link">get an instant freight quote</Link> in under 30 seconds.
              </div>
            </form>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["98.4%", "on-time"],
                ["162", "countries served"],
                ["24/7", "ops desk"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="text-2xl md:text-3xl font-black text-white">{v}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#A9C0E0] font-semibold mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section className="vtx-container py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="vtx-eyebrow">/ Services</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black text-[#0E2F76] tracking-tighter max-w-xl">Every mode. Every leg. One team accountable end-to-end.</h2>
          </div>
          <Link to="/quote" className="vtx-btn-primary" data-testid="services-quote-cta">Get an instant quote</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {services.map((sv) => (
            <ServiceCard key={sv.id} to={`/services/${sv.slug}`} slug={sv.slug} title={sv.title} tag={sv.tag}
              Icon={iconFor(sv.icon)} eta={sv.eta} desc={sv.card_desc} wide={sv.wide} />
          ))}
        </div>
      </section>

      {/* Value bar */}
      <section className="bg-[#0E2F76] text-white py-14">
        <div className="vtx-container grid md:grid-cols-4 gap-6">
          <ValueItem Icon={ShieldCheck} title="Secure & private"
            body="Your shipments, invoices and documents stay private to your account, protected end to end." />
          <ValueItem Icon={Globe2} title="Global network"
            body="Direct agents in 162 countries with a single accountable operator." />
          <ValueItem Icon={Clock3} title="Milestone tracking"
            body="Real-time updates at every step, delivered by email and SMS the moment things move." />
          <ValueItem Icon={Zap} title="Instant quotes"
            body="Accurate air, sea and road pricing in seconds — no phone calls, no waiting." />
        </div>
      </section>

      {/* Ops line */}
      <section className="vtx-container py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="vtx-eyebrow">/ For operations teams</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-black text-[#0E2F76] tracking-tighter">A portal your controllers won't fight with.</h2>
            <ul className="mt-6 space-y-4 text-[#0f172a]">
              {[
                "Every shipment stays private to your account — no mix-ups, no leaks.",
                "Clear shipment lists with mode, ETA and one-click exception handling.",
                "Live status updates you can share with your customers and partners.",
                "A complete history of who did what and when, on every shipment.",
              ].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 shrink-0 bg-[#d41f26] rounded-full" />
                  <span className="text-sm leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/register" className="vtx-btn-primary" data-testid="ops-register-cta">Open a corporate account</Link>
              <Link to="/login" className="vtx-btn-ghost" data-testid="ops-login-cta">I have an account</Link>
            </div>
          </div>
          <div className="relative">
            <div className="vtx-card p-6 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="vtx-eyebrow">Active shipments</div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#166534]">live</span>
              </div>
              <div className="space-y-3">
                {[
                  ["VTX8HD73KP2Q", "Mombasa → Rotterdam", "sea", "AT_HUB"],
                  ["VTX2AF9QLZ4M", "Nairobi → London", "air", "IN_TRANSIT"],
                  ["VTX99RTX0LNS", "Kigali → Dar es Salaam", "road", "OUT_FOR_DELIVERY"],
                ].map(([n, r, m, s]) => (
                  <div key={n} className="flex items-center justify-between border-b border-[#A9C0E0]/40 pb-3 last:border-0">
                    <div>
                      <div className="font-mono text-sm text-[#0E2F76] font-semibold">{n}</div>
                      <div className="text-xs text-[#475569]">{r}</div>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#1D4ED8]">{s.replace(/_/g," ")}</span>
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#475569] mt-0.5">{m}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 -z-10 w-full h-full vtx-diagonal" style={{ borderRadius: 3 }} />
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function ServiceCard({ to, slug, title, tag, Icon, eta, desc, wide }) {
  // Background photo per service, dropped into /public/services/<slug>.jpg. When
  // the file is absent the card falls back to the original light styling.
  const [hasImg, setHasImg] = useState(true);
  const src = `${process.env.PUBLIC_URL || ""}/services/${slug}.jpg`;
  return (
    <Link to={to} data-testid={`service-card-${title.toLowerCase().split(" ")[0]}`}
      className={`group relative overflow-hidden flex flex-col min-h-[240px] vtx-card vtx-card-interactive p-6 ${wide ? "md:col-span-2" : ""}`}
    >
      {hasImg && (
        <>
          <img src={src} alt="" aria-hidden="true" onError={() => setHasImg(false)}
            className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(14,47,118,0.45) 0%, rgba(7,17,43,0.86) 100%)" }} />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <div className={`w-11 h-11 grid place-items-center border ${hasImg ? "bg-white/15 border-white/40" : "bg-[#F4FEFF] border-[#A9C0E0]/60"}`}>
          <Icon size={22} color={hasImg ? "#ffffff" : "#0E2F76"} strokeWidth={2.2} />
        </div>
        <span className={`text-[10px] tracking-widest uppercase font-bold ${hasImg ? "text-[#FFD9DB]" : "text-[#d41f26]"}`}>{tag}</span>
      </div>
      <div className="relative mt-6">
        <div className={`text-xl font-bold ${hasImg ? "text-white" : "text-[#0E2F76]"}`}>{title}</div>
        <div className={`text-sm mt-2 leading-relaxed ${hasImg ? "text-white/85" : "text-[#475569]"}`}>{desc}</div>
      </div>
      <div className="relative mt-auto pt-6 flex items-center justify-between text-xs">
        <span className={`font-mono font-semibold ${hasImg ? "text-white" : "text-[#0E2F76]"}`}>ETA {eta}</span>
        <span className={`font-bold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 ${hasImg ? "text-white" : "text-[#d41f26]"}`}>Read more <ArrowRight size={12}/></span>
      </div>
    </Link>
  );
}

function ValueItem({ Icon, title, body }) {
  return (
    <div>
      <div className="w-10 h-10 grid place-items-center bg-white/10 border border-white/20 mb-4">
        <Icon size={20} strokeWidth={2.2} />
      </div>
      <div className="font-bold">{title}</div>
      <p className="mt-2 text-sm text-[#A9C0E0] leading-relaxed">{body}</p>
    </div>
  );
}
